import { ActionFunction, redirect } from 'react-router-dom';
import { saveData } from './Action/saveData';

// Sentry
import * as Sentry from '@sentry/react';

// Redux - Para marcar la tab como visitada
import { store } from '../../../../../state/configureStore';
import { actionTabs } from '../../../../../state/actions/ui/actionTabs';

// Validacion centralizada
import {
  validateAllFormsFromSessionStorage,
  validateDateCoherenceFromSessionStorage,
  generateValidationSummary,
} from '../../../../../utils/formValidation';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { deleteWholeDirectory } from '../../../../../utils/files';

// Constants
import {
  formActionNaming,
  directoryNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Types
import { OrderType } from '../../../../../types/orders';

// Offline: centinela compartido (lleva el destino) + aviso
import { isOfflineQueuedError } from '../../../../../offline/isOfflineQueuedError';
import { showSuccessToast } from '../../../../../utils/toasts';
import i18n from '../../../../../i18n';

export const AnimalsValidationAction: ActionFunction = async ({
  request,
  params,
}) => {
  const formData = await request.formData();

  try {
    // Marcar la tab como visitada ANTES de validar/guardar
    store.dispatch(actionTabs('animals'));

    // Leer intent: 'save' (solo guardar) o 'validate' (guardar + validar)
    const intent = formData.get('intent') as string | null;

    // ═══════════════════════════════════════════════════════════════
    // VALIDACION CENTRALIZADA: Solo al VALIDAR, no al GUARDAR
    // Guardar permite salvar cambios sin cumplir todos los requisitos
    // ═══════════════════════════════════════════════════════════════
    if (intent !== 'save') {
      console.log('🔍 [AnimalsValidationAction] Iniciando validacion centralizada (intent: validate)...');

      // Feature flag showHoursType - valor por defecto false
      const showHoursType = false;

      const validationResult = await validateAllFormsFromSessionStorage({
        isNewAction: false,
        showHoursType,
        // Actuació bàsica: la pestaña Operarios está oculta y su formulario
        // nunca se persiste. Sin esto la validación la marca como «pestaña sin
        // completar» y el modal navega a una pestaña que el layout vuelve a
        // esconder — bucle sin salida. Va por DATO (el flag lo pone la página
        // desde su loader), no por `pathname`: esta pantalla vive bajo
        // /work-orders/ y el gating por URL del wizard no se cumple aquí.
        skipOperatorsValidation: formData.get('isBasicAction') === 'true',
      });

      console.log('🔍 [AnimalsValidationAction] Resultado de validacion:', {
        isValid: validationResult.isValid,
        errorsCount: validationResult.errors.length,
        tabsWithErrors: validationResult.tabsWithErrors.map((t) => t.tabKey),
      });

      if (!validationResult.isValid) {
        console.log('❌ [AnimalsValidationAction] Validacion fallida, retornando errores');

        return {
          validationFailed: true,
          validationErrors: validationResult.errors,
          tabsWithErrors: validationResult.tabsWithErrors,
          validationSummary: generateValidationSummary(
            validationResult.tabsWithErrors
          ),
        };
      }

      console.log('✅ [AnimalsValidationAction] Validacion exitosa, guardando actuacion...');
    } else {
      console.log('💾 [AnimalsValidationAction] Intent=save, saltando validacion centralizada...');
      // «Guardar» deja guardar a medias, pero NO fechas incoherentes (12-oct,
      // decisión del usuario; así se guardó un fin anterior al inicio en
      // At.0000523.0002.02). Mismas reglas que «Validar» y mismo aviso,
      // apuntando a Operarios (normal) o a «Datos Act» (básica).
      const coherence = await validateDateCoherenceFromSessionStorage({
        isNewAction: false,
        skipOperatorsValidation: formData.get('isBasicAction') === 'true',
      });
      if (!coherence.isValid) {
        return {
          validationFailed: true,
          validationErrors: coherence.errors,
          tabsWithErrors: coherence.tabsWithErrors,
          validationSummary: generateValidationSummary(coherence.tabsWithErrors),
        };
      }
    }

    // BUG #12 FIX: Pasar params para fallback cuando sessionStorage esta vacio
    // C-11.9: `await` para que el catch atrape rejections async.
    return await saveData(formData, params);
  } catch (err) {
    // C-11.9: si SAVE_ACTION cayó por OfflineQueueLink (edit/validate offline),
    // tratar como éxito: redirect al summary del action editado. Apollo
    // replay-eará la mutation al reconectar (cola individual).
    if (isOfflineQueuedError(err)) {
      let actionId: string | undefined;
      try {
        const raw = sessionStorage.getItem(
          formActionNaming.NEW_VALIDATION_ACTION_DATA,
        );
        if (raw) {
          const data = JSON.parse(decompressSessionStorage(raw)) as {
            id?: string;
          };
          actionId = data.id;
        }
      } catch {
        // ignore — fallback a params
      }
      actionId = actionId ?? params?.id;
      // Las fotos ya subieron a Hetzner y viajan plegadas en la mutation
      // encolada (RetryLink) — OPFS ya no hace falta. Sin este borrado, un
      // croquis/foto sobrante bloquea (skip-if-exists) el próximo guardado
      // con el mismo nombre de fichero (cazado en pruebas manuales
      // 2026-07-04, GAP H3).
      deleteWholeDirectory(directoryNaming.CANVAS);
      deleteWholeDirectory(OrderType.Action);
      window.dispatchEvent(new CustomEvent('offline-action-queued'));
      // Aviso al operario (spec 2026-09-11 § 1 E6): hasta ahora este asistente
      // encolaba EN SILENCIO — el evento de arriba no lo escucha nadie.
      showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
      // El destino lo pone quien encoló; si el centinela viene del link (sin
      // `redirectTo`), el resumen de la actuación como siempre.
      const destino = (err as { redirectTo?: string }).redirectTo;
      return redirect(destino ?? `../../summary/${actionId ?? ''}`);
    }
    Sentry.captureException(err);
    console.error('❌ [AnimalsValidationAction] Error:', err);
    return {
      error: true,
      errorMessage: 'Error al guardar la actuacion',
    };
  }
};
